import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Calendar, Clock, Search, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { season1Episodes, SPOTIFY_SHOW_URL } from '../data/season1Episodes';
export function Season1Page() {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredEpisodes = season1Episodes.filter(ep => ep.title.toLowerCase().includes(searchTerm.toLowerCase()) || ep.guest.toLowerCase().includes(searchTerm.toLowerCase()) || (ep.verse ?? '').toLowerCase().includes(searchTerm.toLowerCase()));
  return <PageTransition data-id="element-754" data-mp="3cnf192t59dgn">
      <div className="pt-24 pb-16 min-h-screen" data-id="element-755" data-mp="2f6gc5tajai59">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-756" data-mp="2f5n8502ym6qt">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-12" data-id="element-757" data-mp="2k3osrpg1ae4e">
            <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-758" data-mp="2vow21w14qqrz">
              The Complete Season
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-759" data-mp="2qp9rjucoromv">
              Season 1
            </h1>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-6" data-id="element-760" data-mp="2lobz8oxype7n"></div>
            <p className="text-lg text-white/80 max-w-2xl mx-auto" data-id="element-761" data-mp="2gqai49k3joyt">
              Twenty-one honest conversations about faith lived out — from the
              process of pain to grace that is sufficient in weakness. Every
              episode is anchored in Scripture.
            </p>
          </motion.div>

          {/* Season Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-id="element-762" data-mp="5ztvajup77pu">
            {[{
            label: 'Episodes',
            value: '21'
          }, {
            label: 'Aired',
            value: 'Jan – Jun 2026'
          }, {
            label: 'Status',
            value: 'Complete'
          }].map(stat => <div key={stat.label} className="bg-dark-800 border border-dark-600 rounded-xl p-6 text-center" data-id="element-763" data-mp="l3n6ynh1168d">
                <p className="text-2xl font-serif font-bold text-gold-500 mb-1" data-id="element-764" data-mp="1ctrlw6m0p127">
                  {stat.value}
                </p>
                <p className="text-white/50 text-sm uppercase tracking-wider" data-id="element-765" data-mp="yurw2vmrqg4g">
                  {stat.label}
                </p>
              </div>)}
          </div>

          {/* Search */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-10" data-id="element-766" data-mp="3jj68bc5rj07m">
            <h2 className="text-2xl font-serif font-bold text-white self-start md:self-auto" data-id="element-767" data-mp="3ojkgcu7d6a5n">
              All Season 1 Episodes
            </h2>
            <div className="relative w-full md:w-72" data-id="element-768" data-mp="3h1w1iql69ueg">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-769" data-mp="3bzn7dqwkcmar">
                <Search className="h-5 w-5 text-white/40" data-id="element-770" data-mp="3pzetyg15bb0d" />
              </div>
              <label htmlFor="season1-search" className="sr-only" data-id="element-771" data-mp="3l0v6gb6wlr5w">
                Search Season 1 episodes
              </label>
              <input id="season1-search" type="text" placeholder="Search by title, guest, or verse..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-dark-500 rounded-md leading-5 bg-dark-800 text-white placeholder-white/40 focus:outline-none focus:bg-dark-700 focus:border-gold-500 transition duration-150 ease-in-out sm:text-sm" data-id="element-772" data-mp="2xclq3osglvm5" />
            </div>
          </div>

          {/* Episode Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-773" data-mp="2n9q3jpm9v7ky">
            {filteredEpisodes.map((ep, index) => <motion.div key={ep.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: Math.min(index, 8) * 0.05
          }} data-id="element-774" data-mp="2s8b45q5frt9x" data-mpi={index}>
                <Card className="flex flex-col h-full bg-dark-700 border-dark-600" data-id="element-775" data-mp="3fx2ltnlegt8g">
                  <div className="p-6 flex flex-col h-full" data-id="element-776" data-mp="3kwf929g6o2bk">
                    <div className="flex justify-between items-start mb-4" data-id="element-777" data-mp="3kvxfxloti47v">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gold-500/10 text-gold-500" data-id="element-778" data-mp="1lkwbss48sihq">
                        {ep.category}
                      </span>
                      <span className="text-white/40 text-xs font-mono" data-id="element-779" data-mp="7p4ezdgslhps">
                        EP {ep.id}
                      </span>
                    </div>

                    <h3 className="text-xl font-serif font-bold text-white mb-1 line-clamp-2" data-id="element-780" data-mp="3v041ifa7ys06">
                      {ep.title}
                    </h3>
                    <p className="text-sm text-gold-500 mb-4 font-medium" data-id="element-781" data-mp="3pzg4nczb7gy1">
                      with {ep.guest}
                    </p>

                    <p className="text-white/70 text-sm mb-5 flex-grow line-clamp-3" data-id="element-782" data-mp="mkaowbj0e0oe">
                      {ep.description}
                    </p>

                    {ep.verse && <div className="mb-5 rounded-lg bg-dark-800 border-l-2 border-gold-500 px-4 py-3" data-id="element-783" data-mp="cmwk596g70bx">
                        <p className="flex items-center text-xs font-medium text-gold-500 mb-1" data-id="element-784" data-mp="32bouog18rfzx">
                          <BookOpen className="w-3.5 h-3.5 mr-1.5" data-id="element-785" data-mp="2xd53o17sje0p" />
                          {ep.verse}
                        </p>
                        <p className="text-white/60 text-sm italic leading-relaxed" data-id="element-786" data-mp="2yvc68k5qzphz">
                          {ep.verseText}
                        </p>
                      </div>}

                    <div className="flex items-center justify-between pt-4 border-t border-dark-600 mt-auto" data-id="element-787" data-mp="2psksuetbajnp">
                      <div className="flex items-center space-x-4 text-xs text-white/50" data-id="element-788" data-mp="2uqw1wnbs8q4v">
                        <span className="flex items-center" data-id="element-789" data-mp="2g2rj7jcqcem3">
                          <Calendar className="w-3 h-3 mr-1" data-id="element-790" data-mp="2kut05ikuetig" /> {ep.date}
                        </span>
                        <span className="flex items-center" data-id="element-791" data-mp="273efz8f78m9t">
                          <Clock className="w-3 h-3 mr-1" data-id="element-792" data-mp="270s10npzb4hk" /> {ep.duration}
                        </span>
                      </div>
                      <a href={ep.spotifyUrl} target="_blank" rel="noopener noreferrer" aria-label={`Listen to episode ${ep.id}: ${ep.title}`} className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center text-dark-900 hover:scale-110 transition-transform shadow-lg shadow-gold-500/20" data-id="element-793" data-mp="1x45e7te3e3cw">
                        <Play className="w-4 h-4 fill-current ml-0.5" data-id="element-794" data-mp="1shqu9itcl9ou" />
                      </a>
                    </div>
                  </div>
                </Card>
              </motion.div>)}
          </div>

          {filteredEpisodes.length === 0 && <div className="text-center py-20" data-id="element-795" data-mp="1wij1y7mkn6j0">
              <p className="text-white/60 text-lg" data-id="element-796" data-mp="1wfoiaibdtpqn">
                No Season 1 episodes found matching your search.
              </p>
            </div>}

          {/* Next Season CTA */}
          <div className="mt-16 bg-dark-800 rounded-2xl p-8 md:p-12 text-center border border-dark-600" data-id="element-797" data-mp="269ngx7c8yrdu">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-798" data-mp="2k4zcfgy88lvt">
              That's a wrap on Season 1
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto" data-id="element-799" data-mp="3mjmy25q6jv0g">
              Season 2 is on the way. Follow along on Spotify so you don't miss
              the first episode.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-800" data-mp="d89yybuhw0t">
              <Link to="/podcasts/season-2" data-id="element-801" data-mp="4eths59ivk45">
                <Button data-id="element-802" data-mp="4ejp29x3eyur">Preview Season 2</Button>
              </Link>
              <a href={SPOTIFY_SHOW_URL} target="_blank" rel="noopener noreferrer" data-id="element-803" data-mp="33uzujrnnjdoz">
                <Button variant="outline" data-id="element-804" data-mp="38tjlk6h3rfo7">Follow on Spotify</Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>;
}